"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.userCountsQuerySchema = exports.userListQuerySchema = exports.userListFilterSchema = exports.USER_LIST_ACCOUNT_ACTIVE = exports.USER_LIST_STATUSES = exports.auditQuerySchema = exports.auditFilterSchema = exports.AUDIT_CATEGORY_EVENTS = exports.AUDIT_CATEGORY_LABELS = exports.AUDIT_CATEGORIES = exports.setAvailabilitySchema = exports.blockOutSchema = exports.weeklyAvailabilitySchema = exports.COVER_NOTICE_SWEEP = exports.COVER_RESTORE_WINDOW_SECONDS = exports.COVER_WAITING_TOO_LONG_HOURS = exports.changeDelegationDatesSchema = exports.checkDelegationSchema = exports.createDelegationSchema = exports.updateRoleSchema = exports.createRoleSchema = exports.roleStartFromSchema = exports.BLANK_ROLE_START = exports.permissionCellSchema = exports.reassignOwnershipSchema = exports.deactivateUserSchema = exports.ROLE_ASSIGNMENT_CONFLICT_MESSAGES = exports.setUserRolesSchema = exports.updateUserSchema = exports.inviteUserSchema = exports.roleAssignmentSchema = exports.roleOwnershipSchema = exports.roleScopeKindSchema = exports.assignableRoleKeySchema = exports.impersonateSchema = exports.changePasswordSchema = exports.enrolTwoFactorSchema = exports.verifyOtpSchema = exports.inviteLookupQuerySchema = exports.acceptInviteSchema = exports.resetPasswordSchema = exports.forgotPasswordSchema = exports.loginSchema = void 0;
exports.roleAssignmentConflicts = roleAssignmentConflicts;
exports.auditCategoryOf = auditCategoryOf;
exports.requiresTwoFactor = requiresTwoFactor;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const schemas_1 = require("./schemas");
// S3 and S4 inputs. One definition validates the request on the server and drives
// the React Hook Form resolver in the browser, so the two can never disagree about
// what a valid password is.
//
// Messages come from §L26, and each says what to do rather than what went wrong.
exports.loginSchema = zod_1.z.object({
    email: schemas_1.emailSchema,
    password: zod_1.z.string().min(1, 'This field is required.'),
    /** Only ever offered on a shared-device-free login form. */
    rememberMe: zod_1.z.boolean().optional(),
});
exports.forgotPasswordSchema = zod_1.z.object({
    email: schemas_1.emailSchema,
});
exports.resetPasswordSchema = zod_1.z.object({
    token: zod_1.z.string().min(1, 'This reset link is missing its code.'),
    password: schemas_1.passwordSchema,
});
exports.acceptInviteSchema = zod_1.z.object({
    token: zod_1.z.string().min(1, 'This invite link is missing its code.'),
    password: schemas_1.passwordSchema,
    fullName: zod_1.z.string().trim().min(2, 'Enter your full name.').max(120).optional(),
});
/** `GET /auth/invite?token=` — what the accept-invite screen asks before it shows the form. */
exports.inviteLookupQuerySchema = zod_1.z.object({
    token: zod_1.z.string().trim().min(1, 'This invite link is missing its code.').max(200),
});
/** F1.07: six digits. A recovery code is offered on the same screen, so either fits. */
exports.verifyOtpSchema = zod_1.z.object({
    code: zod_1.z
        .string()
        .trim()
        .min(6, 'Enter the 6-digit code from your authenticator app.')
        .max(20),
    /**
     * Carried over from the sign-in form, because this is where the real session
     * cookie is finally issued: until the code is accepted the browser only holds a
     * five-minute challenge cookie, which is deliberately never persistent.
     */
    rememberMe: zod_1.z.boolean().optional(),
});
exports.enrolTwoFactorSchema = zod_1.z.object({
    code: zod_1.z
        .string()
        .trim()
        .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app.'),
});
exports.changePasswordSchema = zod_1.z.object({
    currentPassword: zod_1.z.string().min(1, 'This field is required.'),
    password: schemas_1.passwordSchema,
});
exports.impersonateSchema = zod_1.z.object({
    userId: schemas_1.uuidSchema,
    /** §L23 logs every impersonation; a stated purpose makes the log worth reading. */
    reason: zod_1.z.string().trim().min(3, 'Say why, in a few words.').max(200),
});
// --- S4: roles, scope and users --------------------------------------------
exports.assignableRoleKeySchema = zod_1.z.enum(enums_1.ASSIGNABLE_ROLE_KEYS);
exports.roleScopeKindSchema = zod_1.z.enum(enums_1.ROLE_SCOPE_KINDS);
exports.roleOwnershipSchema = zod_1.z.enum(enums_1.ROLE_OWNERSHIPS);
/**
 * A role assignment, with its scope.
 *
 * The refinement is the enforcement point for "a role without a scope is invalid".
 * A venture-scoped assignment with no ventures listed is not a wide permission, it
 * is an unanswered question — and the one thing it must never do is quietly mean
 * "everywhere".
 */
exports.roleAssignmentSchema = zod_1.z
    .object({
    roleId: schemas_1.uuidSchema,
    scopeKind: exports.roleScopeKindSchema,
    ownership: exports.roleOwnershipSchema.default('all'),
    ventureIds: zod_1.z.array(schemas_1.uuidSchema).default([]),
    departmentIds: zod_1.z.array(schemas_1.uuidSchema).default([]),
})
    .refine((value) => value.scopeKind !== 'venture' || value.ventureIds.length > 0, { message: 'Pick at least one venture for this role.', path: ['ventureIds'] })
    .refine((value) => value.scopeKind !== 'department' || value.departmentIds.length > 0, { message: 'Pick at least one department for this role.', path: ['departmentIds'] });
exports.inviteUserSchema = zod_1.z.object({
    email: schemas_1.emailSchema,
    fullName: zod_1.z.string().trim().min(2, 'Enter their full name.').max(120),
    /** F2.01: role AND scope at invite time, not afterwards. */
    assignment: exports.roleAssignmentSchema,
    managerUserId: schemas_1.uuidSchema.optional(),
    isPanelEligible: zod_1.z.boolean().default(false),
});
exports.updateUserSchema = zod_1.z.object({
    fullName: zod_1.z.string().trim().min(2, 'Enter their full name.').max(120).optional(),
    displayName: zod_1.z.string().trim().max(120).nullable().optional(),
    phone: zod_1.z.string().trim().max(32).nullable().optional(),
    timezone: zod_1.z.string().trim().min(1).max(64).optional(),
    managerUserId: schemas_1.uuidSchema.nullable().optional(),
    isPanelEligible: zod_1.z.boolean().optional(),
});
exports.setUserRolesSchema = zod_1.z.object({
    assignments: zod_1.z
        .array(exports.roleAssignmentSchema)
        .min(1, 'Give them at least one role, with a scope.'),
});
exports.ROLE_ASSIGNMENT_CONFLICT_MESSAGES = {
    duplicate: 'This role is already given for the same ventures or departments in another row. Remove one of them.',
    covered_by_organisation: 'This role already covers the whole organisation in another row, so this row adds nothing. Remove it.',
};
/**
 * Rows that give a role somebody already has, for a place it already reaches (security audit item 13).
 *
 * The `user_roles_unique` index cannot catch these: every stored row has a null venture or a null
 * department, and Postgres treats nulls as distinct, so it never refuses anything. This is the
 * check that does. Two kinds:
 * - the same role for the same place twice — the whole organisation twice, or one venture or
 *   department listed in two rows (or twice in one), whatever the ownership;
 * - a role for some ventures or departments when the same role already covers the whole
 *   organisation with at least the same reach. "Whole organisation, their own records" next to
 *   "Venture B, all records" is kept: it genuinely sees more in Venture B.
 */
function roleAssignmentConflicts(assignments) {
    const conflicts = [];
    const seen = new Set();
    assignments.forEach((assignment, index) => {
        const places = placesOf(assignment).map((place) => `${assignment.roleId}|${place}`);
        const isDuplicate = places.some((key) => seen.has(key)) || new Set(places).size < places.length;
        for (const key of places)
            seen.add(key);
        if (isDuplicate) {
            conflicts.push({ index, kind: 'duplicate' });
            return;
        }
        const isCovered = assignment.scopeKind !== 'organisation' &&
            assignments.some((other) => other.roleId === assignment.roleId &&
                other.scopeKind === 'organisation' &&
                (other.ownership === 'all' || assignment.ownership === 'own'));
        if (isCovered)
            conflicts.push({ index, kind: 'covered_by_organisation' });
    });
    return conflicts;
}
function placesOf(assignment) {
    if (assignment.scopeKind === 'venture')
        return assignment.ventureIds.map((id) => `venture:${id}`);
    if (assignment.scopeKind === 'department') {
        return assignment.departmentIds.map((id) => `department:${id}`);
    }
    return ['organisation'];
}
/**
 * F2.05/F2.07: deactivation names where the owned records go, before it happens.
 *
 * `reassignToUserId` takes everything; `handover` (Admin redesign, split hand-over) names a person
 * per kind of work and wins for that kind. A kind named in neither stays with nobody, so it is refused.
 */
exports.deactivateUserSchema = zod_1.z.object({
    reassignToUserId: schemas_1.uuidSchema.optional(),
    handover: zod_1.z
        .object({
        requisitions: schemas_1.uuidSchema.optional(),
        jobs: schemas_1.uuidSchema.optional(),
        lineManagedJobs: schemas_1.uuidSchema.optional(),
        tasks: schemas_1.uuidSchema.optional(),
    })
        .optional(),
    reason: zod_1.z.string().trim().max(200).optional(),
});
exports.reassignOwnershipSchema = zod_1.z.object({
    fromUserId: schemas_1.uuidSchema,
    toUserId: schemas_1.uuidSchema,
});
// --- F2.08–F2.10: the role builder ------------------------------------------
exports.permissionCellSchema = zod_1.z.object({
    objectKey: zod_1.z.string().min(1),
    level: zod_1.z.enum(['full', 'scoped', 'read', 'none']),
    scope: zod_1.z.string().min(1).nullable().default(null),
    isReadOnly: zod_1.z.boolean().default(false),
    /** §L23 field paths, e.g. `candidate.expectedSalary`. */
    restrictedFields: zod_1.z.array(zod_1.z.string()).default([]),
})
    // "Their own" always says which own (Admin redesign 2026-11-02): a scoped cell with no scope
    // would be a permission nobody can evaluate. `DEFAULT_PERMISSION_SCOPES` fills a raised row.
    .refine((cell) => cell.level !== 'scoped' || cell.scope !== null, {
    message: 'Say which ones they can open.',
    path: ['scope'],
});
/** ADR 0085: "New role" starts with no access anywhere, or as a copy of a standard role. */
exports.BLANK_ROLE_START = 'blank';
exports.roleStartFromSchema = zod_1.z.enum([exports.BLANK_ROLE_START, ...enums_1.ASSIGNABLE_ROLE_KEYS]);
exports.createRoleSchema = zod_1.z.object({
    label: zod_1.z.string().trim().min(2, 'Give the role a name.').max(80),
    description: zod_1.z.string().trim().max(400).nullable().default(null),
    /**
     * Blank, the standard role whose cells and actions it copies, or (Admin redesign 2026-11-02,
     * "Copy a role") the id of any live role, a copy included.
     */
    startFrom: zod_1.z.union([exports.roleStartFromSchema, schemas_1.uuidSchema]),
});
exports.updateRoleSchema = zod_1.z.object({
    /** The role's `version` as last read. A save from an older one is refused (409), so two Admins can't overwrite each other. */
    version: zod_1.z.number().int().positive(),
    label: zod_1.z.string().trim().min(2, 'Give the role a name.').max(80).optional(),
    description: zod_1.z.string().trim().max(400).nullable().optional(),
    cells: zod_1.z.array(exports.permissionCellSchema).optional(),
    transitions: zod_1.z
        .array(zod_1.z.object({ transitionKey: zod_1.z.string().min(1), isAllowed: zod_1.z.boolean() }))
        .optional(),
});
// --- F2.11: delegation -------------------------------------------------------
const coverFields = {
    /** Whose approvals move. Left out: the person asking. Only an Admin may name somebody else. */
    fromUserId: schemas_1.uuidSchema.optional(),
    toUserId: schemas_1.uuidSchema,
    startsAt: zod_1.z.iso.datetime({ message: 'Pick a start date.' }),
    endsAt: zod_1.z.iso.datetime({ message: 'Pick an end date.' }),
    reason: zod_1.z.string().trim().max(200).nullable().default(null),
};
const endsAfterStart = (value) => new Date(value.endsAt) > new Date(value.startsAt);
const END_AFTER_START = { message: 'The end date must be after the start date.', path: ['endsAt'] };
const SOMEBODY_ELSE = { message: 'Pick somebody else to cover for you.', path: ['toUserId'] };
exports.createDelegationSchema = zod_1.z
    .object(coverFields)
    .refine(endsAfterStart, END_AFTER_START)
    .refine((value) => value.fromUserId !== value.toUserId, SOMEBODY_ELSE);
/**
 * `POST /users/delegations/check`: the same rules as saving, saving nothing, so the page can warn before Save (for example when the
 * stand-in is away too). `delegationId` checks a change of dates: that cover is left out of the overlap.
 */
exports.checkDelegationSchema = zod_1.z
    .object({ ...coverFields, delegationId: schemas_1.uuidSchema.optional() })
    .refine(endsAfterStart, END_AFTER_START)
    .refine((value) => value.fromUserId !== value.toUserId, SOMEBODY_ELSE);
/** `PATCH /users/delegations/:id`: new dates for a cover that has not ended. The rules run again. */
exports.changeDelegationDatesSchema = zod_1.z
    .object({
    startsAt: coverFields.startsAt,
    endsAt: coverFields.endsAt,
    version: zod_1.z.number().int().positive(),
})
    .refine(endsAfterStart, END_AFTER_START);
/** "Waiting too long" on a cover in force: an approval waiting on the away person for more than this. */
exports.COVER_WAITING_TOO_LONG_HOURS = 48;
/**
 * Undo after "End now" or "Cancel" (review R6): `POST /users/delegations/:id/restore` brings the same
 * cover back, but only this long after it was ended. The toast offers Undo for 10 seconds; the rest
 * is room for a slow network.
 */
exports.COVER_RESTORE_WINDOW_SECONDS = 60;
/**
 * When N-69 ("you are covering for…") goes out (review R28): a sweep at five past each hour from
 * 08:05 to 20:05 Bangladesh time tells every stand-in whose cover is in force and who hasn't been
 * told. So a cover starting on a later day is told that morning, one starting today within the hour,
 * and one saved in the evening the next morning, if it is still in force by then.
 */
exports.COVER_NOTICE_SWEEP = { zone: 'Asia/Dhaka', minute: 5, fromHour: 8, toHour: 20 };
// --- F2.12: declared availability -------------------------------------------
/**
 * L5: availability is declared, never read from a calendar API (D5). This is the
 * only source of availability in the system, which is why S19's scheduler depends
 * on it existing from S4 rather than arriving with the scheduler.
 */
exports.weeklyAvailabilitySchema = zod_1.z
    .object({
    /** 0 = Sunday, matching the D14 Sun–Thu working week. */
    dayOfWeek: zod_1.z.number().int().min(0).max(6),
    /** Wall-clock times in the person's own timezone (S19). */
    startTime: zod_1.z.string().regex(/^\d{2}:\d{2}$/, 'Use a time like 09:00.'),
    endTime: zod_1.z.string().regex(/^\d{2}:\d{2}$/, 'Use a time like 17:30.'),
})
    .refine((value) => value.endTime > value.startTime, {
    message: 'The end must be after the start.',
    path: ['endTime'],
});
/**
 * Whole days, first to last inclusive, each sent as that date at 00:00 UTC — so "away
 * on the 5th" is one date twice. S19's scheduler reads them as dates in the person's
 * own timezone.
 */
exports.blockOutSchema = zod_1.z
    .object({
    blockedFrom: zod_1.z.iso.datetime({ message: 'Pick a start date.' }),
    blockedTo: zod_1.z.iso.datetime({ message: 'Pick an end date.' }),
    reason: zod_1.z.string().trim().max(200).nullable().default(null),
})
    .refine((value) => new Date(value.blockedTo) >= new Date(value.blockedFrom), {
    message: "The last day can't be before the first.",
    path: ['blockedTo'],
});
exports.setAvailabilitySchema = zod_1.z.object({
    weekly: zod_1.z
        .array(exports.weeklyAvailabilitySchema)
        .max(21, 'That is more slots than a week has room for.')
        .default([]),
    blockOuts: zod_1.z.array(exports.blockOutSchema).max(100).default([]),
    /**
     * The ids of the rows this edit started from (every row the last read returned). A save
     * replaces every row, so any save since changes them — a stale save is a 409, as every
     * versioned save is (S7). Left out, the save is not checked.
     */
    baseIds: zod_1.z.array(schemas_1.uuidSchema).max(200).optional(),
});
// --- F2.13: the access audit -------------------------------------------------
/**
 * The kinds USER-06 counts and filters by (Admin redesign 2026-11-02, Access log A). Failed
 * sign-ins are their own kind, 2FA failures included; sign-outs count as sign-ins.
 */
exports.AUDIT_CATEGORIES = [
    'sign_in',
    'failed_sign_in',
    'permission_denied',
    'role_change',
    'impersonation',
    'delegation',
    'account_change',
];
exports.AUDIT_CATEGORY_LABELS = {
    sign_in: 'Sign-ins',
    failed_sign_in: 'Failed sign-ins',
    permission_denied: 'Refused actions',
    role_change: 'Role changes',
    impersonation: 'Viewing as someone',
    delegation: 'Cover arrangements',
    account_change: 'Account changes',
};
/** Which event types each category covers. Kept beside the labels it explains. No type is in two. */
exports.AUDIT_CATEGORY_EVENTS = {
    sign_in: ['auth.login_success', 'auth.logout'],
    failed_sign_in: ['auth.login_failed', 'auth.2fa_failed'],
    permission_denied: ['auth.permission_denied'],
    role_change: [
        'user.role_assigned',
        'user.role_removed',
        'role.created',
        'role.updated',
        'role.deleted',
        'role.field_rule_changed',
        'role.transition_rule_changed',
    ],
    impersonation: ['auth.impersonation_started', 'auth.impersonation_ended'],
    // Both parties are in the payload of each, which is what the S4 criterion means
    // by "both appear in the audit trail".
    delegation: ['delegation.created', 'delegation.ended', 'delegation.dates_changed', 'delegation.restored'],
    account_change: [
        'user.invited',
        'user.invite_resent',
        'user.invite_revoked',
        'user.activated',
        'user.updated',
        'user.deactivated',
        'user.reactivated',
        'user.suspended',
        'user.unsuspended',
        'user.password_reset_sent',
        // One per person taking over work when someone is deactivated (split hand-over).
        'ownership.transferred',
        'auth.password_changed',
        'auth.account_locked',
        'auth.account_unlocked',
        'auth.session_revoked',
        'auth.2fa_enrolled',
        // F2.13: reading the audit is itself an event. An audit that hides reads of
        // itself is an audit that can be read quietly, which is the one thing it must
        // not be — so the unfiltered view has to include it. Exporting it, likewise.
        'audit.viewed',
        'audit.exported',
    ],
};
/** The kind an event type is counted under, or null when no kind covers it. */
function auditCategoryOf(eventType) {
    return exports.AUDIT_CATEGORIES.find((category) => exports.AUDIT_CATEGORY_EVENTS[category].includes(eventType)) ?? null;
}
/**
 * The access log's filters. "When" defaults to the last 30 days (`AUDIT_DEFAULT_WINDOW_DAYS`) when
 * `from` is left out. The feed pages by `cursor` — the `nextCursor` of the page before — on
 * (occurredAt, id), newest first, so it reaches every entry rather than stopping at 100.
 */
exports.auditFilterSchema = zod_1.z.object({
    category: zod_1.z.enum(exports.AUDIT_CATEGORIES).optional(),
    /** "Who": everything this person did. */
    actorId: schemas_1.uuidSchema.optional(),
    from: zod_1.z.iso.datetime().optional(),
    to: zod_1.z.iso.datetime().optional(),
    q: zod_1.z.string().trim().max(120).optional(),
});
exports.auditQuerySchema = exports.auditFilterSchema.extend({
    cursor: zod_1.z.string().trim().min(1).max(200).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(200).default(50),
});
/**
 * USER-01's status tiles. `locked` is an active account inside its lockout window, and `active`
 * leaves those out, so the five add up to All.
 */
exports.USER_LIST_STATUSES = ['active', 'invited', 'locked', 'suspended', 'deactivated'];
/**
 * Not a tile: every account that may sign in, locked or not. Pickers (approvers, recruiters, cover)
 * use it, so a person locked out for a few minutes doesn't vanish from them.
 */
exports.USER_LIST_ACCOUNT_ACTIVE = 'account_active';
/** USER-01's filters: what the list shows, what the tiles count and what "all matching" acts on. */
exports.userListFilterSchema = zod_1.z.object({
    q: zod_1.z.string().trim().max(120).optional(),
    status: zod_1.z.enum([...exports.USER_LIST_STATUSES, exports.USER_LIST_ACCOUNT_ACTIVE]).optional(),
    /** A standard role, by its key. Copies of it are not included; they have their own `roleId`. */
    roleKey: zod_1.z.enum(enums_1.ASSIGNABLE_ROLE_KEYS).optional(),
    /** Any role, a copy included ("N people have it · See them"). */
    roleId: schemas_1.uuidSchema.optional(),
    ventureId: schemas_1.uuidSchema.optional(),
    /** On the interview panel (`isPanelEligible`). */
    panel: zod_1.z.enum(['yes', 'no']).optional(),
    twoFactor: zod_1.z.enum(['on', 'off']).optional(),
});
exports.userListQuerySchema = exports.userListFilterSchema.extend({
    page: zod_1.z.coerce.number().int().positive().default(1),
    pageSize: zod_1.z.coerce.number().int().positive().max(200).default(50),
    /** `column:direction`, comma-separated; see `parseUserListSort` in `people.ts`. */
    sort: zod_1.z.string().trim().max(120).optional(),
});
/** The tiles count everything the other filters let through, whatever tile is picked. */
exports.userCountsQuerySchema = exports.userListFilterSchema.omit({ status: true });
/** Convenience for the web: does this set of roles have to enrol in 2FA? */
function requiresTwoFactor(roles) {
    return roles.some((role) => enums_1.TWO_FACTOR_MANDATORY_ROLES.includes(role));
}
//# sourceMappingURL=auth-schemas.js.map